import { useState, useEffect, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useDealsStore } from '@/stores/useDealsStore'
import { useAuthStore } from '@/stores/authStore'
import { useDealSocket } from '@/hooks/useDealSocket'
import { useDealsTeamClosers } from '@/hooks/useDealsTeamClosers'
import {
  GoalsDashboard,
  DealsTableView,
  DealsCalendarView,
  DealsJourneyView,
  DealDetailPanel,
  DealsFilters,
  DealsTopbar,
} from '@/components/Deals'
import { KanbanBoardV2 } from '@/components/DealsV2'
import type { Deal } from '@/types'
import { toast } from 'sonner'
import { filterVisibleDeals } from '@/components/Deals/filterVisibleDeals'
import { canFilterDealsByCloser } from '@/utils/dealsTeamAccess'
import { useDealsPageHandlers } from './useDealsPageHandlers'
import { DealsPageModals } from './DealsPageModals'
import { useCrmValuesVisible } from '@/hooks/useCrmValuesVisible'

export default function Deals() {
  const { user } = useAuthStore()
  const {
    deals,
    setDeals,
    updateDealById,
    removeDealById,
    loading,
    selectedDeal,
    setSelectedDeal,
    viewMode,
    setViewMode,
    mainTab,
    setMainTab,
    closerFilter,
    setCloserFilter,
    searchTerm,
    setSearchTerm,
    showStagnantOnly,
    setShowStagnantOnly,
    etapaFilter,
    setEtapaFilter,
    periodFilter,
    setPeriodFilter,
  } = useDealsStore()

  useDealSocket()

  const {
    handoffDeal,
    setHandoffDeal,
    pendingTransition,
    showLossModal,
    showFutureModal,
    showWinModal,
    showBantModal,
    showStageChoiceModal,
    showContractDataModal,
    contractDataDeal,
    contractDataModalMode,
    fetchDeals,
    fetchMetadata,
    handleMoveStage,
    handleBantSave,
    handleLossSave,
    handleFutureSave,
    handleWinSave,
    handleStageChoice,
    handleContractDataConfirm,
    handleCancelModal,
    handleHandoffAccept,
    handleHandoffReject,
    dealsMeta,
    hasMoreDeals,
    loadingMore,
    loadMoreDeals,
    listError,
  } = useDealsPageHandlers()

  const [isPulseVisible, setIsPulseVisible] = useState(() => {
    const saved = localStorage.getItem('deals_pulse_visible')
    return saved !== null ? JSON.parse(saved) : true
  })

  const { valuesVisible: areValuesVisible, setValuesVisible: setAreValuesVisible } =
    useCrmValuesVisible()

  useEffect(() => {
    localStorage.setItem('deals_pulse_visible', JSON.stringify(isPulseVisible))
  }, [isPulseVisible])

  useEffect(() => {
    setSelectedDeal(null)
    setDeals([])
    fetchDeals()
    fetchMetadata()
    return () => {
      setSelectedDeal(null)
    }
  }, [fetchDeals, fetchMetadata, setDeals, setSelectedDeal])

  const canFilterByCloser = canFilterDealsByCloser(user?.role)
  const { closers: teamClosers } = useDealsTeamClosers(canFilterByCloser)

  const visibleDeals = useMemo(
    () =>
      filterVisibleDeals(deals, {
        mainTab,
        closerFilter,
        searchTerm,
        showStagnantOnly,
        etapaFilter,
        periodFilter,
        userRole: user?.role,
        userId: user?.id,
      }),
    [
      deals,
      mainTab,
      closerFilter,
      showStagnantOnly,
      searchTerm,
      etapaFilter,
      periodFilter,
      user?.role,
      user?.id,
    ],
  )

  const handleSelectDeal = useCallback(
    (deal: Deal) => setSelectedDeal(deal),
    [setSelectedDeal],
  )

  const handleClosePanel = useCallback(() => setSelectedDeal(null), [setSelectedDeal])

  const handleDealUpdated = useCallback(
    (updated: Deal) => updateDealById(updated.id, updated),
    [updateDealById],
  )

  const handleDealDeleted = useCallback(
    (dealId: string) => {
      removeDealById(dealId)
      toast.success('Deal excluído com sucesso')
    },
    [removeDealById],
  )

  return (
    <DashboardLayout title="Deals">
      <AnimatePresence>
        {isPulseVisible && (
          <motion.section
            initial={{ opacity: 0, marginBottom: 0 }}
            animate={{ opacity: 1, marginBottom: 20 }}
            exit={{ opacity: 0, marginBottom: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden min-h-[520px]"
          >
            <GoalsDashboard deals={deals} />
          </motion.section>
        )}
      </AnimatePresence>

      <DealsTopbar
        mainTab={mainTab}
        setMainTab={setMainTab}
        viewMode={viewMode}
        setViewMode={setViewMode}
        isPulseVisible={isPulseVisible}
        setIsPulseVisible={setIsPulseVisible}
        areValuesVisible={areValuesVisible}
        setAreValuesVisible={setAreValuesVisible}
        filters={
          <DealsFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            closerFilter={closerFilter}
            setCloserFilter={setCloserFilter}
            showStagnantOnly={showStagnantOnly}
            setShowStagnantOnly={setShowStagnantOnly}
            etapaFilter={etapaFilter}
            setEtapaFilter={setEtapaFilter}
            periodFilter={periodFilter}
            setPeriodFilter={setPeriodFilter}
            canFilterByCloser={canFilterByCloser}
            mainTab={mainTab}
            closers={teamClosers}
          />
        }
      />

      <div>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="mb-4 h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent" />
            <p className="text-sm font-medium text-text-muted">Sincronizando pipeline...</p>
          </div>
        ) : mainTab === 'calendar' ? (
          <DealsCalendarView visibleDeals={visibleDeals} onSelectDeal={handleSelectDeal} />
        ) : viewMode === 'journey' ? (
          <DealsJourneyView
            visibleDeals={visibleDeals}
            mainTab={mainTab}
            onSelectDeal={handleSelectDeal}
            onMoveStage={handleMoveStage}
            areValuesVisible={areValuesVisible}
            error={listError}
            onRetry={() => void fetchDeals()}
          />
        ) : viewMode === 'kanban' ? (
          <KanbanBoardV2
            visibleDeals={visibleDeals}
            mainTab={mainTab}
            onSelectDeal={handleSelectDeal}
            onMoveStage={handleMoveStage}
            areValuesVisible={areValuesVisible}
            error={listError}
            onRetry={() => void fetchDeals()}
          />
        ) : (
          <DealsTableView
            visibleDeals={visibleDeals}
            onSelectDeal={handleSelectDeal}
            areValuesVisible={areValuesVisible}
          />
        )}

        {!loading && hasMoreDeals && mainTab !== 'calendar' && (
          <div className="mt-4 flex flex-col items-center gap-2">
            <p className="text-xs text-text-muted">
              Exibindo {deals.length} de {dealsMeta.totalItems} deals
            </p>
            <button
              type="button"
              onClick={() => void loadMoreDeals()}
              disabled={loadingMore}
              className="rounded-lg border border-accent/30 bg-accent/10 px-4 py-2 text-sm font-medium text-accent transition-colors hover:bg-accent/20 disabled:opacity-50"
            >
              {loadingMore ? 'Carregando...' : 'Carregar mais deals'}
            </button>
          </div>
        )}
      </div>

      <DealDetailPanel
        deal={selectedDeal}
        onClose={handleClosePanel}
        onDealUpdated={handleDealUpdated}
        onDealDeleted={handleDealDeleted}
      />

      <DealsPageModals
        handoffDeal={handoffDeal}
        onCloseHandoff={() => setHandoffDeal(null)}
        onHandoffAccept={handleHandoffAccept}
        onHandoffReject={handleHandoffReject}
        showLossModal={showLossModal}
        showFutureModal={showFutureModal}
        showWinModal={showWinModal}
        showStageChoiceModal={showStageChoiceModal}
        showContractDataModal={showContractDataModal}
        contractDataDeal={contractDataDeal}
        contractDataModalMode={contractDataModalMode}
        showBantModal={showBantModal}
        pendingTransition={pendingTransition}
        onCancelModal={handleCancelModal}
        onLossSave={handleLossSave}
        onFutureSave={handleFutureSave}
        onWinSave={handleWinSave}
        onStageChoice={handleStageChoice}
        onContractDataConfirm={handleContractDataConfirm}
        onBantSave={handleBantSave}
      />
    </DashboardLayout>
  )
}
